// hook for manually refetching assistant messages when webhook streaming fails

import { useEffect } from "react";
import { DealThreadMessageStatus } from "../graphql/generated";
import {
  differenceInMilliseconds,
  differenceInMinutes,
  fromUnixTime,
} from "date-fns";

export interface ManualRefreshInput {
  messageCreatedAt: number;
  messageLastStreamedAt?: number;
  messageStatus: DealThreadMessageStatus;
  hasVerifyingQuotes?: boolean;
}

export function useManualRefresh(
  input: ManualRefreshInput,
  callback: () => void,
) {
  useEffect(
    () => {
      let manualRefreshTimeout: NodeJS.Timeout | null = null;

      function checkMessage() {
        const cancelledStatuses = [
          DealThreadMessageStatus.Cancelled,
          DealThreadMessageStatus.Failed,
        ];

        if (
          cancelledStatuses.includes(input.messageStatus) ||
          differenceInMinutes(
            new Date(),
            fromUnixTime(input.messageCreatedAt),
          ) > 5
        ) {
          if (manualRefreshTimeout) {
            clearTimeout(manualRefreshTimeout);
          }
          return;
        }

        // Continue refreshing if the message is SENT but has verifying quotes
        const shouldContinueRefreshing =
          input.messageStatus !== DealThreadMessageStatus.Sent ||
          input.hasVerifyingQuotes === true;

        if (!shouldContinueRefreshing) {
          if (manualRefreshTimeout) {
            clearTimeout(manualRefreshTimeout);
          }
          return;
        }

        const mostRecentTimestamp =
          input.messageLastStreamedAt || input.messageCreatedAt;

        if (manualRefreshTimeout) {
          clearTimeout(manualRefreshTimeout);
        }

        if (
          differenceInMilliseconds(
            new Date(),
            fromUnixTime(mostRecentTimestamp),
          ) > 250
        ) {
          callback();
          manualRefreshTimeout = setTimeout(() => {
            checkMessage();
          }, 250);
          return;
        }

        manualRefreshTimeout = setTimeout(checkMessage, 250);
        return;
      }

      checkMessage();

      return () => {
        if (manualRefreshTimeout) {
          clearTimeout(manualRefreshTimeout);
        }
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      input.messageCreatedAt,
      input.messageLastStreamedAt,
      input.messageStatus,
      input.hasVerifyingQuotes,
    ],
  );
}
